'use client';

import './date-range-calendar.css';

import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CalendarLeftActive2424 from '@svg/survey/when/calendar_left_active_24_24.svg';
import CalendarLeftDeactive2424 from '@svg/survey/when/calendar_left_deactive_24_24.svg';
import CalendarRightActive2424 from '@svg/survey/when/calendar_right_active_24_24.svg';

const DateRangeCalendar = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  focus,
  onFocusChange,
  maximumLength,
  minimumDate,
  month,
  onMonthChange,
  holidays,
}: {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  focus: 'startDate' | 'endDate';
  onFocusChange: (focus: 'startDate' | 'endDate') => void;
  maximumLength: number;
  minimumDate: Date | null;
  month?: Date | null;
  onMonthChange?: (date: Date) => void;
  holidays: { date: string; name: string }[];
}) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(month ?? new Date());
  const [mouseOverDate, setMouseOverDate] = useState<Date | null>(null);

  const calendarDates = useMemo(() => {
    const start = dayjs(currentMonth).startOf('month').startOf('week');

    const calendar: Date[] = [];
    let current = start;

    for (let i = 0; i < 42; i++) {
      calendar.push(current.toDate());
      current = current.add(1, 'day');
    }

    return calendar;
  }, [currentMonth]);

  const isHoliday = useCallback(
    (date: Date) => {
      const dateString = dayjs(date).format('YYYY-MM-DD');
      return holidays.some((holiday) => holiday.date === dateString);
    },
    [holidays],
  );

  const onClickDate = useCallback(
    (date: Date) => {
      if (focus === 'startDate') {
        onStartDateChange(date);
        onFocusChange('endDate');
        setMouseOverDate(null);
      } else {
        onEndDateChange(date);
        onFocusChange('startDate');
      }
    },
    [onStartDateChange, onEndDateChange, focus, onFocusChange],
  );

  useEffect(() => {
    // `isTouchDevice` 해당 방식도 가능
    // const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const supportsHover = window.matchMedia('(hover: hover)').matches;
    if (!supportsHover) return;

    const mouseOverListener = (e: MouseEvent) => {
      if (focus !== 'endDate') return;
      let target = e.target as HTMLElement | null;
      if (!target) {
        setMouseOverDate(null);
        return;
      }
      const targetDate = target.getAttribute('data-date');
      if (!targetDate) {
        setMouseOverDate(null);
        return;
      }

      setMouseOverDate(new Date(targetDate));
    };

    document.addEventListener('mouseover', mouseOverListener);

    return () => {
      document.removeEventListener('mouseover', mouseOverListener);
    };
  }, [focus]);

  return (
    <div className='flex flex-col w-full'>
      <div className='flex items-center justify-between border-b border-gray-200 px-24pxr py-12pxr'>
        <button
          className='text-center w-24pxr h-24pxr'
          onClick={() => {
            setCurrentMonth(dayjs(currentMonth).subtract(1, 'month').toDate());
            onMonthChange?.(dayjs(currentMonth).subtract(1, 'month').toDate());
          }}
          disabled={dayjs(currentMonth).isSame(minimumDate, 'month')}
        >
          {dayjs(currentMonth).isSame(minimumDate, 'month') ? <CalendarLeftDeactive2424 /> : <CalendarLeftActive2424 />}
        </button>
        <p className='text-gray-800 sub-heading-16-m'>
          {dayjs(currentMonth).isSame(Date(), 'year')
            ? dayjs(currentMonth).format('MM월')
            : dayjs(currentMonth).format('YYYY년 MM월')}
        </p>
        <button
          className='text-center w-24pxr h-24pxr'
          onClick={() => {
            setCurrentMonth(dayjs(currentMonth).add(1, 'month').toDate());
            onMonthChange?.(dayjs(currentMonth).add(1, 'month').toDate());
          }}
        >
          <CalendarRightActive2424 />
        </button>
      </div>

      <div className='grid grid-cols-7 px-20pxr'>
        {['일', '월', '화', '수', '목', '금', '토'].map((day) => (
          <div
            key={day}
            className='flex items-center justify-center body-14-r w-full aspect-square min-h-40pxr'
            style={{
              color: day === '일' ? '#FF616D' : day === '토' ? '#4287FF' : '#0A0A0A',
            }}
          >
            {day}
          </div>
        ))}
        {calendarDates.map((date) => {
          const isSunday = dayjs(date).day() === 0 || isHoliday(date);
          const isSaturday = dayjs(date).day() === 6;
          const isToday = dayjs(date).isSame(Date(), 'day');
          const isLessThanMinimumDate = dayjs(minimumDate).subtract(1, 'day').endOf('day').isAfter(date);

          let dateClassName = '';
          if (isSunday) dateClassName += '-is-sunday ';
          if (isSaturday) dateClassName += '-is-saturday ';
          if (isToday) dateClassName += '-is-today ';
          if (isLessThanMinimumDate) dateClassName += '-is-less-than-minimum-date ';

          const isStartDate = startDate && dayjs(date).isSame(startDate, 'day');
          const isEndDate = endDate && dayjs(date).isSame(endDate, 'day');
          const isBetween = startDate && endDate && dayjs(date).isAfter(startDate) && dayjs(date).isBefore(endDate);
          const isStartAndEndGapOne = startDate && endDate && dayjs(startDate).add(1, 'day').isSame(endDate, 'day');
          if (isStartDate && isEndDate) dateClassName += '-is-start-and-end -is-selected ';
          else if (isStartDate && isStartAndEndGapOne) dateClassName += '-is-start-gap-one -is-selected ';
          else if (isEndDate && isStartAndEndGapOne) dateClassName += '-is-end-gap-one -is-selected ';
          else if (isStartDate) dateClassName += '-is-start -is-selected ';
          else if (isEndDate) dateClassName += '-is-end -is-selected ';
          else if (isBetween) dateClassName += '-is-between -is-selected ';

          let isOverMaximumLength = false;
          if (startDate && !endDate && !dayjs(startDate).isSame(date)) {
            const diff = Math.abs(dayjs(startDate).diff(date, 'day'));
            if (diff > maximumLength) {
              isOverMaximumLength = true;
              dateClassName += '-is-over-maximum-length ';
            }
          }

          // if (startDate && endDate && dayjs(startDate).isSame(date)) {
          //   dateClassName += '-is-start-with-end';
          // }
          // if (startDate && endDate && dayjs(endDate).isSame(date)) {
          //   dateClassName += '-is-end-with-start';
          // }

          if (mouseOverDate) {
            // 마우스 오버된 후보 endDate가 startDate보다 이후인 경우
            if (dayjs(mouseOverDate).isAfter(startDate)) {
              if (dayjs(date).isAfter(startDate) && dayjs(date).endOf('day').isBefore(dayjs(mouseOverDate))) {
                dateClassName += '-is-between -is-selected ';
              }
            }
            // 마우스 오버된 후보 startDate가 endDate보다 이전인 경우
            else if (dayjs(mouseOverDate).isBefore(startDate)) {
              if (dayjs(date).isAfter(mouseOverDate) && dayjs(date).endOf('day').isBefore(startDate)) {
                dateClassName += '-is-between -is-selected ';
              }
            }
          }

          return (
            <button
              key={date.toISOString()}
              className={`flex items-center justify-center ${dateClassName} sub-heading-16-m text-black -is-calendar-date w-full aspect-square min-h-40pxr`}
              disabled={isLessThanMinimumDate || isOverMaximumLength}
              data-date={isLessThanMinimumDate || isOverMaximumLength ? '' : dayjs(date).format('YYYY-MM-DD')}
            >
              <span
                className='flex items-center justify-center w-full h-full'
                data-date={isLessThanMinimumDate || isOverMaximumLength ? '' : dayjs(date).format('YYYY-MM-DD')}
                onClick={() => onClickDate(date)}
              >
                {dayjs(date).date()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DateRangeCalendar;
